import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const helpers = createMultiStyleConfigHelpers(['root', 'icon', 'wrapper', 'title']);

/** Default and bounds for Contentful `maxItemsPerRowDesktop` (e.g. 3 freeform, 4 range PLP). */
const ICON_SET_DESKTOP_COLS_DEFAULT = 3;
const ICON_SET_DESKTOP_COLS_MIN = 2;
const ICON_SET_DESKTOP_COLS_MAX = 6;
const ICON_SET_DESKTOP_GAP_PX = 40;

/**
 * @param {number|null|undefined} value - Contentful IconSet.maxItemsPerRowDesktop
 * @returns {number}
 */
export function clampIconSetDesktopColumns(value) {
  if (value == null) {
    return ICON_SET_DESKTOP_COLS_DEFAULT;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return ICON_SET_DESKTOP_COLS_DEFAULT;
  }
  return Math.min(
    ICON_SET_DESKTOP_COLS_MAX,
    Math.max(ICON_SET_DESKTOP_COLS_MIN, Math.round(n))
  );
}

const WRAPPER_PADDING = {
  paddingTop: {base: 20, md: 20},
  paddingBottom: {base: 20, md: 20},
  paddingLeft: {base: 5, md: 5},
  paddingRight: {base: 5, md: 5},
};

const SHARED_ROOT_BASE = {
  width: '100%',
  maxWidth: 'iconSetMax',
  margin: '0 auto',
  backgroundColor: 'transparent',
  display: 'flex',
  flexWrap: 'wrap',
  gap: {base: 12, md: `${ICON_SET_DESKTOP_GAP_PX}px`},
  alignItems: 'stretch',
};

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null }, lastRowIsOdd?: boolean }} props - style props from useMultiStyleConfig
 * @returns {object}
 */
function getGridRoot(props) {
  const lastRowIsOdd = Boolean(props?.lastRowIsOdd);
  return {
    ...SHARED_ROOT_BASE,
    justifyContent: {
      base: 'center',
      md: lastRowIsOdd ? 'center' : 'flex-start',
    },
  };
}

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null } }} props - style props from useMultiStyleConfig
 * @returns {object}
 */
function getIconItemStyles(props) {
  const desktopCols = clampIconSetDesktopColumns(props?.component?.maxItemsPerRowDesktop);
  const desktopBasis = `calc((100% - (${desktopCols} - 1) * ${ICON_SET_DESKTOP_GAP_PX}px) / ${desktopCols})`;
  const mobileBasis = 'calc((100% - var(--chakra-space-12, 3rem)) / 2)';

  return {
    gap: 4,
    width: {base: mobileBasis, md: desktopBasis},
    flexBasis: {base: mobileBasis, md: desktopBasis},
    minWidth: {base: mobileBasis, md: desktopBasis},
    maxWidth: {base: mobileBasis, md: desktopBasis},
  };
}

function getWrapperStyles({component}) {
  const bgHex = normalizeHex(component?.backgroundColour);
  const textHex = normalizeHex(component?.textColour);

  return {
    width: '100%',
    backgroundColor: bgHex ?? 'white',
    ...(textHex && {color: textHex}),
    ...WRAPPER_PADDING,
  };
}

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null }, lastRowIsOdd?: boolean }} props
 * @returns {{wrapper: object, root: object, icon: object}}
 */
function getVariantStyles(props) {
  return {
    wrapper: getWrapperStyles(props),
    root: getGridRoot(props),
    icon: getIconItemStyles(props),
  };
}

/**
 * IconSet – two layouts:
 * - icons: iconsCollection (legacy) – full-width wrapper, content max 1142px; desktop columns from maxItemsPerRowDesktop (default 3).
 * - valueProp: explainerBlocksCollection – same grid; Icon entries use valueProp styling.
 * backgroundColour and textColour (Contentful) apply to wrapper; text inherits to Icon.
 */
export const iconSetStyles = helpers.defineMultiStyleConfig({
  baseStyle({component}) {
    const bgHex = normalizeHex(component?.backgroundColour);
    const textHex = normalizeHex(component?.textColour);

    return {
      root: {
        backgroundColor: bgHex ?? 'white',
        overflow: 'hidden',
        ...(textHex && {color: textHex}),
      },
      wrapper: {},
      title: {
        display: 'none',
      },
      icon: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 0,
      },
    };
  },
  variants: {
    icons: getVariantStyles,
    valueProp: getVariantStyles,
  },
  defaultProps: {
    variant: 'icons',
  },
});
